/**
 * Jämförelsekluster (AEO/SEO).
 *
 * Central, typad källa för alla /jamfor/<slug>-sidor som byggs på den
 * delade `comparisonRoute`-helpern. Två typer:
 *
 *   - kind: "model"      → jämför arbetssätt/affärsmodeller (sakligt, neutralt)
 *   - kind: "competitor" → jämför Fennys modell mot en namngiven aktörs
 *                          typiska modell. INGA påhittade betyg, priser eller
 *                          recensioner — endast verifierbara, generella
 *                          skillnader i arbetssätt + tydlig disclaimer.
 *
 * Varje sida måste ha unik title/description/H1/kort svar (doorway-skydd).
 */

export interface ComparisonRow {
  /** Jämförelsedimension, t.ex. "Modell", "Tempo", "Pris". */
  dimension: string;
  /** Hur Fenny gör. */
  fenny: string;
  /** Hur motparten/alternativet typiskt gör. */
  other: string;
}

export interface Comparison {
  slug: string;
  kind: "model" | "competitor";
  /** Namnet på motparten/alternativet (kolumnrubrik + i text). */
  otherName: string;
  /** Eyebrow ovanför H1. */
  eyebrow: string;
  /** Synlig H1. */
  title: string;
  /** <title> i head (≤60 tecken rekommenderat). */
  metaTitle: string;
  /** meta description (≤160 tecken). */
  metaDescription: string;
  /** Citerbart kort svar (1–3 meningar) — renderas som QuickAnswer. */
  shortAnswer: string;
  /** 1–2 stycken brödtext under kort svar. */
  intro: string[];
  /** Jämförelsetabell. */
  rows: ComparisonRow[];
  /** "När Fenny passar bättre". */
  fennyBetterWhen: string[];
  /** "När alternativet passar bättre". */
  otherBetterWhen: string[];
  /** "Så väljer du rätt". */
  howToChoose: string[];
  /** Answer-library-ids → synlig FAQ + FAQPage JSON-LD. */
  faqIds: string[];
  /** Breadcrumb-etikett (kort). */
  breadcrumbLabel: string;
}



// ---------------------------------------------------------------------------
// Modelljämförelser — neutrala, principiella skillnader i arbetssätt.
// ---------------------------------------------------------------------------

const MODEL_COMPARISONS: Comparison[] = [
  {
    slug: "film-pa-abonnemang-vs-filmbyra",
    kind: "model",
    otherName: "Traditionell filmbyrå",
    eyebrow: "Modelljämförelse",
    title: "Film på abonnemang vs traditionell filmbyrå",
    metaTitle: "Film på abonnemang vs filmbyrå | Fenny",
    metaDescription:
      "Löpande filmabonnemang eller projekt per offert? Jämför tempo, budget, kontinuitet och produktionsvärde — och se när vilken modell vinner.",
    shortAnswer:
      "Film på abonnemang ger jämn produktion till en fast månadskostnad och passar team som behöver film löpande. En traditionell filmbyrå offererar per projekt och passar enskilda, större produktioner med högt produktionsvärde.",
    intro: [
      "Skillnaden handlar inte om kvalitet utan om rytm och budgetlogik. Abonnemang gör film till en löpande funktion med förutsägbar kostnad. Projektmodellen gör varje film till en separat investering med egen offert, planering och uppstart.",
      "De flesta växande marknadsavdelningar behöver båda: ett abonnemang för det kontinuerliga flödet och projektproduktioner för de stora lanseringarna.",
    ],
    rows: [
      { dimension: "Affärsmodell", fenny: "Fast månadskostnad, löpande leverans", other: "Offert per produktion" },
      { dimension: "Tempo", fenny: "Veckotakt — brief till leverans på dagar", other: "Veckor till månader per projekt" },
      { dimension: "Budget", fenny: "Förutsägbar och planerbar", other: "Varierar per projekt och omfång" },
      { dimension: "Kontinuitet", fenny: "Samma team, jämn rytm året runt", other: "Nytt upplägg vid varje uppdrag" },
      { dimension: "Bäst för", fenny: "Löpande social-, sälj- och employer branding-film", other: "Enskilda kampanjer och stora produktioner" },
    ],
    fennyBetterWhen: [
      "Ni behöver film varje eller varannan vecka.",
      "Budgeten ska vara förutsägbar och enkel att försvara internt.",
      "Ni vill slippa starta om planering och offert vid varje film.",
    ],
    otherBetterWhen: [
      "Ni har ett enstaka, stort produktionsbehov per år.",
      "Filmen kräver omfattande regi, location och efterarbete i toppskikt.",
      "Volymen är för låg för att fylla ett abonnemang.",
    ],
    howToChoose: [
      "Räkna på er faktiska volym över ett år — inte per kampanj.",
      "Behöver ni 8+ filmer/år vinner abonnemang nästan alltid på styckkostnad.",
      "Kombinera: abonnemang för flödet, projekt för lanseringarna.",
    ],
    faqIds: [
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
      "sv_vad_ar_film_pa_abonnemang",
      "sv_skillnaden_fenny_filmbyra",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    breadcrumbLabel: "Abonnemang vs filmbyrå",
  },
  {
    slug: "videoabonnemang-vs-contentabonnemang",
    kind: "model",
    otherName: "Contentabonnemang",
    eyebrow: "Modelljämförelse",
    title: "Videoabonnemang vs contentabonnemang",
    metaTitle: "Videoabonnemang vs contentabonnemang — vad ingår?",
    metaDescription:
      "Ett videoabonnemang levererar färdig film löpande. Ett contentabonnemang täcker bredare innehåll. Se vad som ingår, vad som skiljer och vad ni behöver.",
    shortAnswer:
      "Ett videoabonnemang fokuserar helt på löpande filmproduktion — inspelning, klipp och versionering. Ett contentabonnemang är bredare och kan blanda text, bild, grafik och film, oftast med mindre filmdjup per leverans.",
    intro: [
      "Båda är prenumerationsmodeller, men de optimerar för olika saker. Videoabonnemanget är byggt för att producera film med rätt produktionsvärde i jämn takt. Contentabonnemanget sprider budgeten över fler innehållstyper.",
      "Om film är ert viktigaste format ger ett renodlat videoabonnemang mer filmproduktion per krona och en tydligare kvalitetsnivå.",
    ],
    rows: [
      { dimension: "Fokus", fenny: "Film: inspelning, klipp, versionering", other: "Brett innehåll: text, bild, grafik, viss film" },
      { dimension: "Produktionsvärde film", fenny: "Konsekvent, produktionsteam bakom varje film", other: "Varierar — film är ofta ett av flera spår" },
      { dimension: "Leverans", fenny: "Färdiga filmer i jämn takt", other: "Blandad innehållsmix per månad" },
      { dimension: "Bäst för", fenny: "Team där film driver kanalerna", other: "Team som vill täcka många format med en leverantör" },
    ],
    fennyBetterWhen: [
      "Film är ert primära format i sociala kanaler och sälj.",
      "Ni vill ha jämn, hög filmkvalitet utan att tunna ut budgeten.",
      "Ni har redan text/bild internt men saknar filmkapacitet.",
    ],
    otherBetterWhen: [
      "Ni saknar all innehållsproduktion och vill ha en bred partner.",
      "Film är bara en mindre del av behovet.",
    ],
    howToChoose: [
      "Fråga vad som faktiskt driver era resultat — om det är film, välj videoabonnemang.",
      "Lägg ihop hur mycket riktig film ett contentabonnemang levererar per månad.",
      "Renodlat fokus ger oftast högre filmkvalitet per krona.",
    ],
    faqIds: [
      "sv_hur_fungerar_videoabonnemang",
      "sv_vad_ar_film_pa_abonnemang",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_hur_ofta_bor_foretag_publicera_video",
    ],
    breadcrumbLabel: "Video- vs contentabonnemang",
  },
  {
    slug: "video-as-a-service-vs-filmbyra",
    kind: "model",
    otherName: "Traditionell filmbyrå",
    eyebrow: "Modelljämförelse",
    title: "Video as a Service vs traditionell filmbyrå",
    metaTitle: "Video as a Service vs filmbyrå | Fenny",
    metaDescription:
      "Video as a Service gör film till en löpande tjänst med fast kostnad. Jämför mot den klassiska projektbyrån — tempo, skalbarhet, budget och ansvar.",
    shortAnswer:
      "Video as a Service (VaaS) levererar film som en löpande, abonnerad tjänst med fast kostnad, ett dedikerat team och förutsägbar takt. En traditionell filmbyrå arbetar projektbaserat med offert, uppstart och leverans per produktion.",
    intro: [
      "VaaS lånar logiken från SaaS: du prenumererar på en kapacitet i stället för att köpa enskilda projekt. Det gör produktion, planering och budget förutsägbar och lätt att skala upp eller ner.",
      "Projektbyrån är fortfarande oslagbar för enstaka, stora produktioner — men dyrare och långsammare för det löpande behov som de flesta marknadsteam faktiskt har.",
    ],
    rows: [
      { dimension: "Leveransform", fenny: "Löpande tjänst, abonnemang", other: "Enskilda projekt per offert" },
      { dimension: "Kostnad", fenny: "Fast, förutsägbar månadskostnad", other: "Varierar per produktion" },
      { dimension: "Skalbarhet", fenny: "Justera nivå upp/ner mellan månader", other: "Ny offert och planering varje gång" },
      { dimension: "Team", fenny: "Dedikerat, lär känna varumärket över tid", other: "Sätts ihop per projekt" },
      { dimension: "Bäst för", fenny: "Kontinuerligt filmbehov i flera kanaler", other: "Enstaka stora kampanjer/filmer" },
    ],
    fennyBetterWhen: [
      "Ni behöver film löpande och vill ha en stabil kapacitet.",
      "Budget och planering måste vara förutsägbar.",
      "Ni vill att teamet lär känna varumärket över tid.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka stor produktion utan löpande fortsättning.",
      "Filmen kräver specialistresurser bara för det enskilda projektet.",
    ],
    howToChoose: [
      "Är behovet återkommande? Då vinner VaaS på pris, tempo och kontinuitet.",
      "Är det en engångsproduktion? Då kan projektbyrå räcka.",
      "Många kombinerar VaaS för flödet och projekt för de stora dragen.",
    ],
    faqIds: [
      "sv_vad_ar_video_as_a_service",
      "sv_hur_fungerar_videoabonnemang",
      "sv_skillnaden_fenny_filmbyra",
      "sv_varfor_lopande_film",
    ],
    breadcrumbLabel: "VaaS vs filmbyrå",
  },
  {
    slug: "filmproduktion-abonnemang-vs-projektpris",
    kind: "model",
    otherName: "Projektpris",
    eyebrow: "Modelljämförelse",
    title: "Filmproduktion på abonnemang vs projektpris",
    metaTitle: "Abonnemang vs projektpris för film | Fenny",
    metaDescription:
      "Ska ni betala fast månadskostnad eller per projekt för filmproduktion? Jämför total kostnad, tempo, risk och planering — och se brytpunkten.",
    shortAnswer:
      "Abonnemang ger lägst total kostnad och högst tempo när ni behöver film löpande, eftersom planering och uppstart inte görs om varje gång. Projektpris passar enstaka produktioner där behovet inte återkommer.",
    intro: [
      "Brytpunkten är volym. Vid några få filmer per år är projektpris ofta enklast. När behovet blir återkommande äter uppstart, offert och planering upp tid och pengar — och då vinner abonnemanget.",
      "Abonnemang gör också budgeten förutsägbar, vilket är lättare att försvara internt än en rad oregelbundna projektfakturor.",
    ],
    rows: [
      { dimension: "Prislogik", fenny: "Fast månadskostnad", other: "Offert per film" },
      { dimension: "Total kostnad vid volym", fenny: "Lägre — inga upprepade uppstarter", other: "Högre — varje projekt bär sin overhead" },
      { dimension: "Tempo", fenny: "Snabbt, inarbetat flöde", other: "Beror på ny planering varje gång" },
      { dimension: "Risk", fenny: "Förutsägbar budget", other: "Oregelbundna toppar" },
      { dimension: "Bäst för", fenny: "Återkommande filmbehov", other: "Enstaka produktioner" },
    ],
    fennyBetterWhen: [
      "Ni vet att ni behöver film flera gånger per år.",
      "Ni vill ha en budget som inte hoppar månad till månad.",
      "Tempo och kontinuitet är viktigt för kanalerna.",
    ],
    otherBetterWhen: [
      "Det är en engångsfilm utan fortsättning.",
      "Behovet är för litet för att motivera ett abonnemang.",
    ],
    howToChoose: [
      "Lista era filmbehov för ett helt år, inte per tillfälle.",
      "Räkna in uppstart och planering — inte bara produktionspriset.",
      "Vid 8+ filmer/år är abonnemang nästan alltid billigast totalt.",
    ],
    faqIds: [
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
      "sv_vad_kostar_videoproduktion",
      "sv_vad_kostar_filmproduktion",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    breadcrumbLabel: "Abonnemang vs projektpris",
  },
  {
    slug: "livsberattelse-pa-film-vs-memoarbok",
    kind: "model",
    otherName: "Memoarbok",
    eyebrow: "Modelljämförelse",
    title: "Livsberättelse på film vs memoarbok",
    metaTitle: "Film eller memoarbok? Så bevarar ni berättelsen",
    metaDescription:
      "Filmad livsberättelse eller memoarbok? Jämför vad varje format bevarar, hur processen ser ut och vad som passar er familj bäst.",
    shortAnswer:
      "En filmad livsberättelse bevarar rösten, ansiktet och berättarsättet — en memoarbok bevarar texten. Filmen gör att kommande generationer faktiskt får möta personen, inte bara läsa om hen.",
    intro: [
      "Båda formaten är legitima sätt att bevara en livshistoria. Skillnaden ligger i vad som faktiskt fastnar i materialet — och hur stort åtagande det är för den som ska berätta.",
      "Många familjer kombinerar: en filmad livsberättelse som primär dokumentation, kompletterad med en transkription om man vill ha läsbart material också.",
    ],
    rows: [
      { dimension: "Bevarar", fenny: "Röst, ansikte, kroppsspråk, berättarsätt", other: "Text och struktur" },
      { dimension: "Form", fenny: "Filmad intervju, klippt till film + kapitel", other: "Skriven memoar, eventuellt med foton" },
      { dimension: "Insats för personen", fenny: "Samtal i egen takt, vi ställer frågorna", other: "Krävande skrivarbete, ofta över lång tid" },
      { dimension: "Ledtid", fenny: "4–8 veckor från första samtal", other: "Månader till år, beroende på upplägg" },
      { dimension: "Bäst för", fenny: "Att bevara personen som hen är", other: "Att bevara ett genomarbetat narrativ i text" },
    ],
    fennyBetterWhen: [
      "Ni vill att kommande generationer ska få höra rösten och se personen.",
      "Den som ska berätta orkar inte ett stort skrivprojekt.",
      "Tiden är begränsad och ni vill ha något på plats snart.",
    ],
    otherBetterWhen: [
      "Personen vill själv skriva och tycker det är meningsfullt.",
      "Familjen vill ha ett läsbart, redigerat narrativ snarare än samtal.",
    ],
    howToChoose: [
      "Fråga personen som ska berätta vad som känns mest motiverande.",
      "Tänk på vad ni faktiskt vill att barnbarnen ska kunna ta del av.",
      "Kombinera gärna: börja med en filmad intervju och komplettera med text.",
    ],
    faqIds: [
      "sv_skillnad_livsberattelse_memoarbok",
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_hur_lang_tid_tar_livsberattelse",
      "sv_vad_ar_fenny_heritage",
    ],
    breadcrumbLabel: "Livsberättelse vs memoarbok",
  },
  {
    slug: "kunskapsoverforing-vs-process-dokumentation",
    kind: "model",
    otherName: "Skriftlig processdokumentation",
    eyebrow: "Modelljämförelse",
    title: "Kunskapsöverföring på film vs skriftlig processdokumentation",
    metaTitle: "Kunskapsöverföring på film vs dokumentation | Fenny",
    metaDescription:
      "Filmad kunskapsöverföring eller skriftlig processdokumentation? Jämför vad varje format bevarar, hur det används och när vilket passar bäst.",
    shortAnswer:
      "Skriftlig processdokumentation är bra på rutiner och steg-för-steg, men förlorar nyanser och tyst kunskap. Filmad kunskapsöverföring bevarar resonemang, principer och erfarenhet på ett sätt som faktiskt går att lära av.",
    intro: [
      "De flesta organisationer dokumenterar processer i text. Det fungerar för rutiner — men sällan för det som är värdefullt när en nyckelperson lämnar: bedömningar, principer, fallgropar och hur man läser av en situation.",
      "Film fångar resonemanget. Text fångar stegen. För många kritiska roller behövs båda.",
    ],
    rows: [
      { dimension: "Bevarar", fenny: "Resonemang, principer, tyst kunskap", other: "Steg, rutiner, checklistor" },
      { dimension: "Användning", fenny: "Onboarding, intern utbildning, kulturarbete", other: "Manualer, support, kvalitetsarbete" },
      { dimension: "Tid att producera", fenny: "Veckor — intervjuer och klipp", other: "Månader om det görs grundligt internt" },
      { dimension: "Risk", fenny: "Materialet behöver underhållas/kompletteras vid stora ändringar", other: "Skriven text åldras snabbt och läses sällan" },
      { dimension: "Bäst för", fenny: "Roller där erfarenhet och omdöme är avgörande", other: "Roller där rutinerna är välspecificerade" },
    ],
    fennyBetterWhen: [
      "En person har erfarenhet som är svår att skriva ner.",
      "Det handlar om generationsskifte, grundarbyte eller pension.",
      "Ni vill att nya medarbetare faktiskt ska lära sig av tidigare nyckelpersoner.",
    ],
    otherBetterWhen: [
      "Det handlar om rutiner, IT-flöden eller juridiskt definierade processer.",
      "Materialet måste vara sök- och redigerbart i löpande drift.",
    ],
    howToChoose: [
      "Identifiera vilka roller där erfarenhet, inte rutin, är det värdefulla.",
      "Använd film för resonemanget, text för stegen.",
      "Börja med en person och en kritisk roll — bygg ut när värdet är bevisat.",
    ],
    faqIds: [
      "sv_hur_gor_man_kunskapsoverforing_pa_film",
      "sv_vad_ar_fenny_heritage",
      "sv_vem_passar_heritage",
    ],
    breadcrumbLabel: "Kunskapsöverföring vs dokumentation",
  },
  {
    slug: "livsberattelse-pa-film-vs-ljudinspelning",
    kind: "model",
    otherName: "Ljudinspelning",
    eyebrow: "Modelljämförelse",
    title: "Livsberättelse på film vs ljudinspelning",
    metaTitle: "Film eller ljudinspelning för livsberättelsen?",
    metaDescription:
      "Filma eller bara spela in ljud när en livsberättelse ska bevaras? Jämför vad formaten bevarar, insats, kostnad och hur de faktiskt används i familjen.",
    shortAnswer:
      "Ljudinspelning bevarar rösten och är snabb och billig att göra själv. Film bevarar dessutom ansikte, blick och kroppsspråk — och används i praktiken oftare av familjen, eftersom en film går att visa tillsammans.",
    intro: [
      "En ljudinspelning är alltid bättre än ingenting, och för många familjer är det rätt första steg: telefonen räcker, och berättelsen finns bevarad redan i kväll.",
      "Skillnaden märks över tid. Ljud lyssnas sällan om, medan en klippt film visas vid högtider och släktträffar. Det är formatets användning, inte kvaliteten i sig, som avgör om berättelsen lever vidare.",
    ],
    rows: [
      { dimension: "Bevarar", fenny: "Röst, ansikte, blick, kroppsspråk, miljö", other: "Röst och tonfall" },
      { dimension: "Insats", fenny: "Planerad inspelningsdag, 2–4 timmar", other: "Kan göras spontant med mobil" },
      { dimension: "Kostnad", fenny: "Från 35 000 kr för en klippt film", other: "I princip gratis om ni gör det själva" },
      { dimension: "Efterarbete", fenny: "Klippning, kapitel, ljud och färg ingår", other: "Ofta ingen redigering alls" },
      { dimension: "Används i praktiken", fenny: "Visas tillsammans vid högtider och släktträffar", other: "Lyssnas sällan om efter inspelningen" },
    ],
    fennyBetterWhen: [
      "Ni vill att barnbarn ska få se personen, inte bara höra rösten.",
      "Materialet ska visas för en samlad familj vid ett tillfälle.",
      "Ni vill ha en färdig, kapitlad film i stället för timmar av råmaterial.",
    ],
    otherBetterWhen: [
      "Det brådskar och ni behöver spela in i dag, utan planering.",
      "Personen är obekväm framför kamera men gärna pratar.",
      "Budgeten är noll och ni gör allt själva.",
    ],
    howToChoose: [
      "Spela in ljud direkt om hälsan är osäker — vänta inte på en produktion.",
      "Välj film när berättelsen ska bevaras för generationer och visas tillsammans.",
      "Kombinera: gör en ljudinspelning nu och en filmad intervju när ni hunnit planera.",
    ],
    faqIds: [
      "sv_hur_bevara_livsberattelse",
      "sv_hur_spela_in_livsberattelse_sjalv",
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_vilka_fragor_livsberattelseintervju",
    ],
    breadcrumbLabel: "Film vs ljudinspelning",
  },
  {
    slug: "kunskapsarv-internt-vs-extern-filmbyra",
    kind: "model",
    otherName: "Intern dokumentation",
    eyebrow: "Modelljämförelse",
    title: "Kunskapsarv internt vs med extern filmbyrå",
    metaTitle: "Kunskapsarv internt eller med extern byrå? | Fenny",
    metaDescription:
      "Ska ni dokumentera kunskapen själva eller ta in en extern part? Jämför frågedesign, ärlighet i svaren, ledtid, kostnad och hur materialet faktiskt används.",
    shortAnswer:
      "Intern dokumentation är billigare och har djupare sakkunskap, men blir sällan av och tenderar att ge inövade svar. En extern intervjuare ställer de naiva frågorna, håller tidsplanen och levererar material som faktiskt går att använda i onboarding.",
    intro: [
      "Nästan alla organisationer har försökt dokumentera kunskap internt. Problemet är sällan viljan utan prioriteringen: dokumentationen får stå tillbaka för det operativa tills personen redan har slutat.",
      "Den andra skillnaden är frågorna. En intern kollega delar redan förkunskapen och hoppar därför över just det som en ny medarbetare behöver höra. Den externa intervjuarens okunskap är en tillgång.",
    ],
    rows: [
      { dimension: "Frågedesign", fenny: "Naiva frågor tvingar fram det underförstådda", other: "Delad förkunskap gör att självklarheter hoppas över" },
      { dimension: "Svaren", fenny: "Ofta öppnare inför en utomstående", other: "Kan bli inövade eller politiskt anpassade" },
      { dimension: "Ledtid", fenny: "Bokad plan med fasta inspelningsdatum", other: "Skjuts ofta upp tills personen slutat" },
      { dimension: "Kostnad", fenny: "60 000–250 000 kr beroende på omfattning", other: "Intern tid, ofta underskattad" },
      { dimension: "Resultat", fenny: "Kapitlat, klippt material för onboarding", other: "Långa råinspelningar som sällan ses" },
    ],
    fennyBetterWhen: [
      "Kunskapen sitter hos en person som lämnar inom ett år.",
      "Materialet ska användas i onboarding och intern utbildning.",
      "Interna försök redan har skjutits upp mer än en gång.",
    ],
    otherBetterWhen: [
      "Det handlar om rutiner som redan är välbeskrivna.",
      "Ni har en intern funktion med tid, metod och utrustning avsatt.",
      "Innehållet är så känsligt att ingen extern part kan medverka.",
    ],
    howToChoose: [
      "Utgå från risken: vilken roll skulle ta längst tid att bygga upp igen?",
      "Börja med en person och en kritisk roll innan ni bygger ut.",
      "Kombinera: extern intervju för resonemanget, intern dokumentation för stegen.",
    ],
    faqIds: [
      "sv_hur_dokumentera_tyst_kunskap",
      "sv_hur_langt_fore_pension_kunskapsoverforing",
      "sv_varfor_racker_inte_skriftlig_kunskapsdokumentation",
      "sv_vad_ar_kunskapsarv",
    ],
    breadcrumbLabel: "Internt vs extern byrå",
  },
];

// ---------------------------------------------------------------------------
// Konkurrentjämförelser — sakliga, modellbaserade. Disclaimer renderas
// alltid (comparisonRoute). Ingen påhittad data, inga betyg.
// ---------------------------------------------------------------------------

interface CompetitorSeed {
  slug: string;
  name: string;
  /** Kort, neutral positionsbeskrivning (verifierbar/allmän). */
  positioning: string;
  metaTitle: string;
  metaDescription: string;
  shortAnswer: string;
  intro: string[];
  /** Hur motparten typiskt arbetar (kolumn 2 i tabellen). */
  otherModel: {
    model: string;
    tempo: string;
    budget: string;
    relation: string;
    bestFor: string;
  };
  fennyBetterWhen: string[];
  otherBetterWhen: string[];
}

const FENNY_COL = {
  model: "Film på abonnemang (Pulse) + projektproduktion (Prime)",
  tempo: "Veckotakt i abonnemang, brief till leverans på dagar",
  budget: "Fast, förutsägbar månadskostnad — projekt offereras separat",
  relation: "Dedikerat team som lär känna varumärket löpande",
  bestFor: "Marknadsteam med kontinuerligt filmbehov i flera kanaler",
};

function competitorToComparison(c: CompetitorSeed): Comparison {
  return {
    slug: c.slug,
    kind: "competitor",
    otherName: c.name,
    eyebrow: "Jämförelse",
    title: `Fenny vs ${c.name} — så skiljer sig modellerna`,
    metaTitle: c.metaTitle,
    metaDescription: c.metaDescription,
    shortAnswer: c.shortAnswer,
    intro: c.intro,
    rows: [
      { dimension: "Modell", fenny: FENNY_COL.model, other: c.otherModel.model },
      { dimension: "Tempo", fenny: FENNY_COL.tempo, other: c.otherModel.tempo },
      { dimension: "Budget", fenny: FENNY_COL.budget, other: c.otherModel.budget },
      { dimension: "Relation", fenny: FENNY_COL.relation, other: c.otherModel.relation },
      { dimension: "Passar bäst för", fenny: FENNY_COL.bestFor, other: c.otherModel.bestFor },
    ],
    fennyBetterWhen: c.fennyBetterWhen,
    otherBetterWhen: c.otherBetterWhen,
    howToChoose: [
      "Utgå från om behovet är löpande eller en engångsproduktion.",
      `Stäm av aktuellt erbjudande och villkor direkt med ${c.name}.`,
      "Boka ett kort möte med oss så räknar vi på ert faktiska filmbehov.",
    ],
    faqIds: [
      "sv_vad_ar_fenny",
      "sv_skillnaden_fenny_filmbyra",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
      "sv_vilka_foretag_passar_fenny",
    ],
    breadcrumbLabel: `Fenny vs ${c.name}`,
  };
}

const COMPETITOR_SEEDS: CompetitorSeed[] = [
  {
    slug: "fenny-vs-klara-fardiga-film",
    name: "Klara Färdiga Film",
    positioning: "filmbyrå med abonnemangsupplägg för löpande film",
    metaTitle: "Fenny vs Klara Färdiga Film | Jämförelse",
    metaDescription:
      "Fenny vs Klara Färdiga Film: jämför arbetssätt, tempo och budgetlogik för löpande filmproduktion. Saklig modelljämförelse — boka möte för en offert.",
    shortAnswer:
      "Både Fenny och Klara Färdiga Film erbjuder löpande filmproduktion. Fenny kombinerar ett filmabonnemang (Pulse) för det kontinuerliga flödet med skräddarsydd projektproduktion (Prime) för större filmer, med dedikerat team och förutsägbar månadskostnad.",
    intro: [
      "Det här är en saklig jämförelse av arbetssätt, inte en värdering. Båda aktörerna arbetar med film till företag och marknadsavdelningar.",
      "Det som avgör valet är hur ert behov ser ut: löpande flöde, enstaka stora produktioner — eller en kombination.",
    ],
    otherModel: {
      model: "Filmproduktion för företag, ofta i abonnemangs- och paketform",
      tempo: "Varierar med upplägg och paket",
      budget: "Beror på vald paket-/upplägg",
      relation: "Beror på kund- och teamupplägg",
      bestFor: "Företag som vill ha löpande film via paketerade upplägg",
    },
    fennyBetterWhen: [
      "Ni vill ha både abonnemang och projektproduktion hos samma partner.",
      "Förutsägbar månadskostnad och veckotakt är avgörande.",
      "Ni vill att teamet bygger upp varumärkeskännedom över tid.",
    ],
    otherBetterWhen: [
      "Deras specifika paket matchar exakt det ni söker.",
      "Ni redan har en upparbetad relation och fungerande flöde där.",
    ],
  },
  {
    slug: "fenny-vs-filmbyran",
    name: "Filmbyrån",
    positioning: "filmbyrå för företagsfilm och reklam",
    metaTitle: "Fenny vs Filmbyrån | Jämförelse",
    metaDescription:
      "Fenny vs Filmbyrån: en saklig jämförelse av modell, tempo och budget för företagsfilm och reklamfilm. Se när abonnemang slår projekt — och tvärtom.",
    shortAnswer:
      "Fenny erbjuder film både på abonnemang och som projekt, med fast månadskostnad för det löpande. En traditionell filmbyrå som Filmbyrån arbetar oftast projektbaserat per offert, vilket passar enstaka större produktioner.",
    intro: [
      "Jämförelsen fokuserar på arbetssätt och affärsmodell — inte på betyg eller pris, som varierar per uppdrag.",
      "För återkommande filmbehov är abonnemang ofta effektivare; för enstaka stora produktioner kan projektmodellen passa bättre.",
    ],
    otherModel: {
      model: "Projektbaserad filmproduktion per offert",
      tempo: "Veckor till månader per projekt",
      budget: "Offert per produktion",
      relation: "Team sätts ihop per projekt",
      bestFor: "Enskilda kampanjer och större produktioner",
    },
    fennyBetterWhen: [
      "Ni behöver film löpande, inte bara vid enstaka tillfällen.",
      "Budgeten ska vara förutsägbar månad för månad.",
      "Ni vill slippa starta om planering vid varje film.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka, stor produktion.",
      "Filmen kräver omfattande specialistresurser bara för det projektet.",
    ],
  },
  {
    slug: "fenny-vs-colibri-content",
    name: "Colibri Content",
    positioning: "content- och videobyrå",
    metaTitle: "Fenny vs Colibri Content | Jämförelse",
    metaDescription:
      "Fenny vs Colibri Content: jämför renodlat videoabonnemang mot bredare content-upplägg. Se vad som ger mest filmkvalitet per krona för ert team.",
    shortAnswer:
      "Fenny är specialiserat på film — på abonnemang och som projekt. En bredare contentbyrå kan täcka fler innehållstyper, men ett renodlat videoabonnemang ger oftast mer filmproduktion och jämnare filmkvalitet per krona.",
    intro: [
      "Det här jämför inriktning och modell. Bredd kan vara en fördel om ni saknar all innehållsproduktion, men en nackdel om film är det som driver era resultat.",
      "Vill ni ha film med konsekvent produktionsvärde i jämn takt är specialisering oftast att föredra.",
    ],
    otherModel: {
      model: "Bredare content-/videoproduktion",
      tempo: "Varierar med innehållsmix",
      budget: "Beror på upplägg och omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Team som vill täcka många innehållsformat",
    },
    fennyBetterWhen: [
      "Film är ert viktigaste format.",
      "Ni vill ha jämn, hög filmkvalitet utan att tunna ut budgeten.",
      "Ni vill ha både abonnemang och projektfilm hos samma partner.",
    ],
    otherBetterWhen: [
      "Ni behöver en bred partner för all sorts innehåll.",
      "Film är bara en mindre del av behovet.",
    ],
  },
  {
    slug: "fenny-vs-parable",
    name: "Parable",
    positioning: "video- och innehållsbyrå",
    metaTitle: "Fenny vs Parable — jämför filmproduktion | Fenny",
    metaDescription:
      "Fenny vs Parable: saklig jämförelse av filmabonnemang och projektproduktion mot alternativets modell. Hitta rätt upplägg för ert filmbehov.",
    shortAnswer:
      "Fenny kombinerar ett filmabonnemang för löpande produktion med skräddarsydd projektfilm. Vilket alternativ som passar er beror på om behovet är kontinuerligt eller enstaka — och hur viktig en fast, förutsägbar budget är.",
    intro: [
      "Jämförelsen beskriver modell och arbetssätt, inte betyg eller priser.",
      "Stäm alltid av aktuellt erbjudande direkt med respektive aktör innan beslut.",
    ],
    otherModel: {
      model: "Video-/innehållsproduktion enligt eget upplägg",
      tempo: "Varierar med projekt och upplägg",
      budget: "Beror på omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Företag vars behov matchar deras inriktning",
    },
    fennyBetterWhen: [
      "Ni vill ha löpande film till förutsägbar månadskostnad.",
      "Ni vill ha både abonnemang och projekt hos en partner.",
      "Veckotakt och kontinuitet är viktigt.",
    ],
    otherBetterWhen: [
      "Deras inriktning matchar exakt det ni söker.",
      "Behovet är en enstaka produktion.",
    ],
  },
  {
    slug: "fenny-vs-streamrocket",
    name: "Streamrocket",
    positioning: "video- och produktionsbyrå",
    metaTitle: "Fenny vs Streamrocket | Jämförelse",
    metaDescription:
      "Fenny vs Streamrocket: jämför filmabonnemang och projektproduktion mot alternativets arbetssätt. Se vilken modell som passar ert filmbehov bäst.",
    shortAnswer:
      "Fenny erbjuder film både löpande (abonnemang) och som projekt, med dedikerat team och fast månadskostnad för det kontinuerliga. Rätt val beror på om ni behöver jämn produktion eller enstaka, större filmer.",
    intro: [
      "Detta är en modelljämförelse — inte en värdering eller prisjämförelse.",
      "Bekräfta alltid aktuella villkor direkt med respektive leverantör.",
    ],
    otherModel: {
      model: "Video-/produktionsuppdrag enligt eget upplägg",
      tempo: "Varierar med projekt",
      budget: "Beror på omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Företag vars behov matchar deras inriktning",
    },
    fennyBetterWhen: [
      "Ni behöver film löpande, inte bara vid enstaka tillfällen.",
      "Förutsägbar budget och snabb leverans är avgörande.",
      "Ni vill ha både abonnemang och projekt hos samma team.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka produktion.",
      "Deras specifika inriktning matchar det ni söker.",
    ],
  },
  {
    slug: "fenny-vs-street-studios",
    name: "Street Studios",
    positioning: "produktions- och innehållsbyrå",
    metaTitle: "Fenny vs Street Studios | Jämförelse",
    metaDescription:
      "Fenny vs Street Studios: saklig jämförelse av modell och arbetssätt för film. Se när löpande abonnemang slår projekt — och när det inte gör det.",
    shortAnswer:
      "Fenny gör film på abonnemang och som projekt, med fast månadskostnad för det löpande flödet. Vilket alternativ som passar beror på er volym, ert tempo och hur förutsägbar budget ni vill ha.",
    intro: [
      "Jämförelsen rör modell och arbetssätt, inte betyg eller pris.",
      "Stäm av aktuellt erbjudande direkt med respektive aktör innan ni väljer.",
    ],
    otherModel: {
      model: "Produktions-/innehållsuppdrag enligt eget upplägg",
      tempo: "Varierar med projekt",
      budget: "Beror på omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Företag vars behov matchar deras inriktning",
    },
    fennyBetterWhen: [
      "Ni behöver jämn filmproduktion över tid.",
      "Budget och planering ska vara förutsägbar.",
      "Ni vill ha abonnemang och projekt hos en partner.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka, avgränsad produktion.",
      "Deras inriktning matchar exakt det ni söker.",
    ],
  },
  {
    slug: "fenny-vs-filmmakeriet",
    name: "Filmmakeriet",
    positioning: "filmproduktionsbolag",
    metaTitle: "Fenny vs Filmmakeriet | Jämförelse",
    metaDescription:
      "Fenny vs Filmmakeriet: jämför abonnemangsbaserad filmproduktion mot projektmodell. Hitta rätt upplägg för löpande eller enstaka filmbehov.",
    shortAnswer:
      "Fenny erbjuder film på abonnemang för löpande behov och projektproduktion för enstaka, större filmer. Ett klassiskt produktionsbolag arbetar oftast projektbaserat, vilket passar enskilda produktioner väl.",
    intro: [
      "Detta beskriver skillnader i modell och arbetssätt, inte kvalitet eller pris.",
      "För återkommande behov vinner abonnemang oftast på tempo och total kostnad.",
    ],
    otherModel: {
      model: "Projektbaserad filmproduktion",
      tempo: "Veckor till månader per projekt",
      budget: "Offert per produktion",
      relation: "Team per projekt",
      bestFor: "Enstaka, större produktioner",
    },
    fennyBetterWhen: [
      "Ni behöver film löpande i flera kanaler.",
      "Ni vill ha förutsägbar månadskostnad.",
      "Veckotakt är viktigt för era kanaler.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka, stor produktion.",
      "Filmen kräver specialistresurser bara för projektet.",
    ],
  },
  {
    slug: "fenny-vs-social-cut",
    name: "Social Cut",
    positioning: "byrå för social video och kortformat",
    metaTitle: "Fenny vs Social Cut | Jämförelse",
    metaDescription:
      "Fenny vs Social Cut: jämför heltäckande filmabonnemang mot specialiserad social video. Se vad ni får per krona och vilken bredd ni behöver.",
    shortAnswer:
      "Fenny levererar film över hela skalan — från social video till reklam och employer branding — på abonnemang och som projekt. En specialist på social video är smal men vass; Fenny täcker fler format med ett dedikerat team.",
    intro: [
      "Jämförelsen handlar om bredd och modell, inte betyg.",
      "Behöver ni enbart kortformat kan en specialist räcka; behöver ni hela filmbredden vinner en heltäckande partner.",
    ],
    otherModel: {
      model: "Specialiserad social video / kortformat",
      tempo: "Snabbt inom sitt format",
      budget: "Beror på upplägg",
      relation: "Beror på kundupplägg",
      bestFor: "Team som enbart behöver social video",
    },
    fennyBetterWhen: [
      "Ni behöver fler format än bara social video.",
      "Ni vill ha både löpande flöde och större produktioner.",
      "Ni vill samla all filmproduktion hos en partner.",
    ],
    otherBetterWhen: [
      "Behovet är uteslutande social video/kortformat.",
      "Ni har redan resten av filmproduktionen löst.",
    ],
  },
  {
    slug: "fenny-vs-brandfixers",
    name: "Brandfixers",
    positioning: "varumärkes- och innehållsbyrå",
    metaTitle: "Fenny vs Brandfixers | Jämförelse",
    metaDescription:
      "Fenny vs Brandfixers: saklig jämförelse av filmfokuserat abonnemang mot bredare varumärkes-/innehållsbyrå. Se vad som ger mest film per krona.",
    shortAnswer:
      "Fenny är specialiserat på film, på abonnemang och som projekt. En bredare varumärkes-/innehållsbyrå kan täcka mer än film, men ett renodlat filmfokus ger oftast mer produktion och jämnare kvalitet inom film.",
    intro: [
      "Jämförelsen beskriver inriktning och modell, inte betyg eller pris.",
      "Bredd är en fördel om ni saknar all produktion — specialisering om film är det viktigaste.",
    ],
    otherModel: {
      model: "Bredare varumärkes-/innehållsarbete",
      tempo: "Varierar med uppdrag",
      budget: "Beror på omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Team som vill ha bred varumärkespartner",
    },
    fennyBetterWhen: [
      "Film är ert viktigaste format.",
      "Ni vill ha jämn filmkvalitet och förutsägbar kostnad.",
      "Ni vill ha både abonnemang och projektfilm.",
    ],
    otherBetterWhen: [
      "Ni behöver en bred partner för hela varumärkesarbetet.",
      "Film är bara en del av behovet.",
    ],
  },
  {
    slug: "fenny-vs-andeox",
    name: "Andeox",
    positioning: "video-/produktionsbyrå",
    metaTitle: "Fenny vs Andeox — jämför filmproduktion | Fenny",
    metaDescription:
      "Fenny vs Andeox: jämför filmabonnemang och projektproduktion mot alternativets modell. Se vilket upplägg som passar ert filmbehov bäst.",
    shortAnswer:
      "Fenny erbjuder film både löpande (abonnemang) och som projekt, med dedikerat team och fast månadskostnad för det kontinuerliga. Rätt val beror på om behovet är återkommande eller en engångsproduktion.",
    intro: [
      "Detta är en modelljämförelse — inte en värdering eller prisjämförelse.",
      "Bekräfta alltid aktuella villkor direkt med respektive leverantör.",
    ],
    otherModel: {
      model: "Video-/produktionsuppdrag enligt eget upplägg",
      tempo: "Varierar med projekt",
      budget: "Beror på omfattning",
      relation: "Beror på kundupplägg",
      bestFor: "Företag vars behov matchar deras inriktning",
    },
    fennyBetterWhen: [
      "Ni behöver film löpande till förutsägbar kostnad.",
      "Ni vill ha både abonnemang och projekt hos samma team.",
      "Tempo och kontinuitet är viktigt.",
    ],
    otherBetterWhen: [
      "Behovet är en enstaka produktion.",
      "Deras inriktning matchar exakt det ni söker.",
    ],
  },
];

const COMPETITOR_COMPARISONS: Comparison[] = COMPETITOR_SEEDS.map(
  competitorToComparison,
);

export const COMPARISONS: ReadonlyArray<Comparison> = [
  ...MODEL_COMPARISONS,
  ...COMPETITOR_COMPARISONS,
];

const BY_SLUG = new Map(COMPARISONS.map((c) => [c.slug, c]));

export function getComparison(slug: string): Comparison {
  const c = BY_SLUG.get(slug);
  if (!c) throw new Error(`Unknown comparison slug: ${slug}`);
  return c;
}

export function getComparisonSlugs(): string[] {
  return COMPARISONS.map((c) => c.slug);
}
